import React from 'react';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { BuildProgress as BuildProgressType, BuildResult, BUILD_STAGES, formatBuildSize, formatBuildTime } from '../../../../core/types/build.types';
import { cn } from '../../../utils/cn';

interface Props {
  progress: BuildProgressType;
  result: BuildResult | null;
  isBuilding: boolean;
  onOpenFolder?: () => void;
  onRunBuild?: () => void;
}

export const BuildProgress: React.FC<Props> = ({
  progress,
  result,
  isBuilding,
  onOpenFolder,
  onRunBuild,
}) => {
  if (!isBuilding && !result) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      {isBuilding && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 size={16} className="text-blue-400 animate-spin" />
              <span className="text-sm text-zinc-200">{progress.message}</span>
            </div>
            <span className="text-sm text-zinc-400">{Math.round(progress.progress)}%</span>
          </div>

          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress.progress}%` }}
            />
          </div>

          {/* Stage indicators */}
          <div className="flex justify-between">
            {BUILD_STAGES.map((stage, index) => {
              const currentIndex = BUILD_STAGES.findIndex(s => s.id === progress.stage);
              const isComplete = index < currentIndex;
              const isCurrent = index === currentIndex;

              return (
                <div
                  key={stage.id}
                  className={cn(
                    'flex items-center gap-1 text-xs',
                    isComplete ? 'text-green-400' : isCurrent ? 'text-blue-400' : 'text-zinc-600'
                  )}
                >
                  {isComplete ? (
                    <CheckCircle size={12} />
                  ) : isCurrent ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <div className="w-3 h-3 rounded-full border border-current" />
                  )}
                  <span className="hidden sm:inline">{stage.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Result */}
      {result && !isBuilding && (
        <div className={cn(
          'p-4 rounded-lg border',
          result.success
            ? 'bg-green-900/20 border-green-500/30'
            : 'bg-red-900/20 border-red-500/30'
        )}>
          <div className="flex items-start gap-3">
            {result.success ? (
              <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
            ) : (
              <XCircle size={20} className="text-red-400 flex-shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              <h4 className={cn(
                'text-sm font-medium',
                result.success ? 'text-green-300' : 'text-red-300'
              )}>
                {result.success ? 'Build Successful!' : 'Build Failed'}
              </h4>

              {result.success && (
                <div className="mt-2 space-y-1 text-xs text-zinc-400">
                  <p>Output: {result.outputPath}</p>
                  <p>Total size: {formatBuildSize(result.totalSize)}</p>
                  <p>Build time: {formatBuildTime(result.buildTime)}</p>
                  <p>Files: {result.files.length}</p>
                </div>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="mt-2">
                  {result.errors.map((error, i) => (
                    <p key={i} className="text-xs text-red-400">{error}</p>
                  ))}
                </div>
              )}

              {/* Warnings */}
              {result.warnings.length > 0 && (
                <div className="mt-2 flex items-start gap-1">
                  <AlertTriangle size={12} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    {result.warnings.map((warning, i) => (
                      <p key={i} className="text-xs text-yellow-400">{warning}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {result.success && (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={onOpenFolder}
                    className="px-3 py-1.5 text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded"
                  >
                    Open Folder
                  </button>
                  <button
                    onClick={onRunBuild}
                    className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded"
                  >
                    Run Build
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
